'use client';

import * as React from 'react';

import { createEventBus } from '@/shared/utils';
import { LearningPaths as LearningPathsDict } from '@/shared/i18n';
import {
  LearningPathByIdScreen,
  LearningPathByIdEvents,
} from 'src/modules/learning-paths/learning-path-by-id';
import { LearningPathByIdEventKeys } from 'src/modules/learning-paths/learning-path-by-id/events';
import { useRouter } from 'next/navigation';
import { useServerAuth } from '@/auth';
import { useLearningPathByIdStore } from 'src/modules/learning-paths/learning-path-by-id/store/learning-path-by-id-store';
import { useFirebaseAuth } from 'src/shared/hooks/use-firebase-auth';
import DetailsLessonsSkeleton from 'src/shared/components/loading-skeleton/details-lesson';
import DetailsSummarySkeleton from 'src/shared/components/loading-skeleton/details-summary';

interface LearningPathByIdContainerProps {
  dict: LearningPathsDict;
  learningPathDocumentId: string;
  learningPathName: string;
}

function LearningPathByIdContainer({
  dict,
  learningPathDocumentId,
  learningPathName,
}: LearningPathByIdContainerProps) {
  const [accessToken, setAccessToken] = React.useState<string>();
  const learningPathByIdEventBus = createEventBus<LearningPathByIdEvents>('learning-path-by-id');
  const router = useRouter();
  const { user, hydrated } = useServerAuth();
  const resetStore = useLearningPathByIdStore((state) => state.resetStore);
  const handleRedirect = React.useCallback((url: string) => router.push(url), [router]);
  const auth = useFirebaseAuth();

  const handleBackNavigation = React.useCallback(() => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push('/percorsi');
    }
  }, [router]);

  React.useEffect(() => {
    const fetchToken = async () => {
      if (auth && auth.currentUser) {
        const token = await auth.currentUser.getIdToken(true);
        setAccessToken(token);
      }
    };

    // Redirect to login if user is not authenticated
    if (hydrated && !user?.email) {
      handleRedirect('/auth/login');
    } else {
      fetchToken();
    }

    if (user?.email) {
      learningPathByIdEventBus.emit(LearningPathByIdEventKeys.customer.view, {
        email: user.email,
      });
    }

    learningPathByIdEventBus.on(LearningPathByIdEventKeys.redirects.to, handleRedirect);
    learningPathByIdEventBus.on(LearningPathByIdEventKeys.redirects.back, handleBackNavigation);

    return () => {
      resetStore();
      learningPathByIdEventBus.off(LearningPathByIdEventKeys.redirects.to, handleRedirect);
      learningPathByIdEventBus.off(LearningPathByIdEventKeys.redirects.back, handleBackNavigation);
    };
  }, [learningPathByIdEventBus, user]);

  if (!hydrated || !auth) {
    return (
      <>
        <DetailsSummarySkeleton />
        <div className="max-w-[800px] m-auto">
          <div className="my-12">
            <DetailsLessonsSkeleton />
          </div>
        </div>
      </>
    );
  }

  return (
    <LearningPathByIdScreen
      auth={auth}
      key="learning-path-by-id"
      dict={dict}
      learningPathName={learningPathName}
      learningPathDocumentId={learningPathDocumentId}
      eventBus={learningPathByIdEventBus}
      accessToken={accessToken}
    />
  );
}

export default LearningPathByIdContainer;
